// ============================================================
// postureAnalyzer.ts — 姿勢分析核心引擎
// 輸入：MediaPipe PoseLandmarker 偵測到的 33 個關鍵點
// 輸出：完整的 PostureAnalysisResult（評分 + 問題 + 評級）
// ============================================================

import type { NormalizedLandmark } from '@mediapipe/tasks-vision';
import {
    PostureAnalysisResult,
    PostureIssue,
    PostureIssueType,
    PostureScoreBreakdown,
    PostureGrade,
    IssueSeverity,
} from 'types/posture';
import { POSE_LANDMARKS, POSTURE_STANDARDS, GRADE_THRESHOLDS } from './poseConstants';

// ── 幾何計算工具函式 ───────────────────────────────────────

/**
 * 計算兩點之間的夾角（以 B 點為頂點，A-B-C 形成的角度）
 * 回傳單位：度（0-180°）
 */
function calculateAngleDeg(
    a: NormalizedLandmark,
    b: NormalizedLandmark,
    c: NormalizedLandmark
): number {
    const radians =
        Math.atan2(c.y - b.y, c.x - b.x) - Math.atan2(a.y - b.y, a.x - b.x);
    let angle = Math.abs((radians * 180) / Math.PI);
    if (angle > 180) angle = 360 - angle;
    return angle;
}

/**
 * 計算向量與垂直方向（Y軸）的偏角
 * 用於評估脊椎是否垂直
 * 回傳單位：度
 */
function calculateVerticalDeviation(
    top: NormalizedLandmark,
    bottom: NormalizedLandmark
): number {
    const dx = bottom.x - top.x;
    const dy = bottom.y - top.y;
    // atan2(dx, dy) 計算與垂直方向的偏角
    return Math.abs((Math.atan2(dx, dy) * 180) / Math.PI);
}

/**
 * 計算兩個關鍵點的中間點
 */
function midpoint(a: NormalizedLandmark, b: NormalizedLandmark): NormalizedLandmark {
    return {
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
        z: (a.z + b.z) / 2,
        visibility: Math.min(a.visibility ?? 1, b.visibility ?? 1),
    };
}

/**
 * 根據偏差值和標準閾值計算分項分數（0-100）
 * @param deviation 實際偏差值（越小越好）
 * @param standard  對應的 POSTURE_STANDARDS 條目
 */
function scoreFromDeviation(
    deviation: number,
    standard: { excellent: number; good: number; fair: number; poor: number }
): number {
    const abs = Math.abs(deviation);
    if (abs <= standard.excellent) return 100;
    if (abs <= standard.good)      return 90 - ((abs - standard.excellent) / (standard.good - standard.excellent)) * 10;
    if (abs <= standard.fair)      return 80 - ((abs - standard.good) / (standard.fair - standard.good)) * 10;
    if (abs <= standard.poor)      return 70 - ((abs - standard.fair) / (standard.poor - standard.fair)) * 10;
    // 超過 poor 閾值：線性衰減至最低分 10
    return Math.max(10, 60 - (abs - standard.poor) * 3);
}

/**
 * 根據偏差值判斷問題嚴重程度
 */
function determineSeverity(
    deviation: number,
    mediumThreshold: number,
    highThreshold: number
): IssueSeverity {
    const abs = Math.abs(deviation);
    if (abs >= highThreshold)   return IssueSeverity.HIGH;
    if (abs >= mediumThreshold) return IssueSeverity.MEDIUM;
    return IssueSeverity.LOW;
}

/**
 * 將總分轉換為評級（S/A/B/C/D）
 */
function scoreToGrade(score: number): PostureGrade {
    if (score >= 90) return 'S';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    return 'D';
}

// ── 主要分析函式 ───────────────────────────────────────────

/**
 * 主要姿勢分析函式
 * 接受 MediaPipe 偵測結果的 landmarks 陣列，回傳完整分析結果
 *
 * @param landmarks - NormalizedLandmark[] (33 個關鍵點，坐標範圍 0-1)
 * @returns PostureAnalysisResult | null（若關鍵點不足則回傳 null）
 */
export function analyzePose(
    landmarks: NormalizedLandmark[]
): PostureAnalysisResult | null {
    // 確保關鍵點數量充足（BlazePose 全身模型需要 33 個）
    if (!landmarks || landmarks.length < 33) return null;

    const lm = landmarks;
    const issues: PostureIssue[] = [];
    const scores: PostureScoreBreakdown = { head: 100, shoulders: 100, spine: 100, hips: 100 };

    // ─── 1. 頭部前傾評估（Forward Head Posture） ─────────────
    // 原理：比較耳朵中心點與肩膀中心點的 X 軸偏移
    // 若耳朵在肩膀前方（X 較大），表示頭部前傾
    const earMid      = midpoint(lm[POSE_LANDMARKS.LEFT_EAR], lm[POSE_LANDMARKS.RIGHT_EAR]);
    const shoulderMid = midpoint(lm[POSE_LANDMARKS.LEFT_SHOULDER], lm[POSE_LANDMARKS.RIGHT_SHOULDER]);
    // 轉換為百分比便於跨不同解析度比較
    const headForwardOffset = (earMid.x - shoulderMid.x) * 100;

    scores.head = scoreFromDeviation(headForwardOffset, POSTURE_STANDARDS.headForwardOffset);

    if (Math.abs(headForwardOffset) > POSTURE_STANDARDS.headForwardOffset.fair) {
        issues.push({
            type: PostureIssueType.HEAD_FORWARD,
            severity: determineSeverity(headForwardOffset, 4, 6),
            message: `頭部前傾 ${Math.abs(headForwardOffset).toFixed(1)}% — 請輕收下巴，想像頭頂被線向上拉，耳朵與肩膀成一直線`,
            exercise: 'chin_tuck',
            offsetValue: headForwardOffset,
        });
    }

    // ─── 2. 肩膀水平評估 ────────────────────────────────────
    // 原理：比較左右肩膀的 Y 坐標差值
    // Y 值差距越大 = 肩膀越不水平（圖像坐標，Y 向下為正）
    const shoulderLevelDiff =
        Math.abs(lm[POSE_LANDMARKS.LEFT_SHOULDER].y - lm[POSE_LANDMARKS.RIGHT_SHOULDER].y) * 100;

    scores.shoulders = scoreFromDeviation(shoulderLevelDiff, POSTURE_STANDARDS.shoulderLevel);

    if (shoulderLevelDiff > POSTURE_STANDARDS.shoulderLevel.fair) {
        issues.push({
            type: PostureIssueType.SHOULDER_UNEVEN,
            severity: determineSeverity(shoulderLevelDiff, 5, 8),
            message: `肩膀不水平（偏差 ${shoulderLevelDiff.toFixed(1)}%）— 放鬆雙肩，想像肩膀像衣架般水平展開`,
            exercise: 'shoulder_roll',
            offsetValue: shoulderLevelDiff,
        });
    }

    // ─── 3. 脊椎垂直度評估 ──────────────────────────────────
    // 原理：計算肩膀中心 → 髖骨中心的向量與垂直方向的夾角
    // 角度越小 = 脊椎越接近垂直 = 姿勢越好
    const hipMid = midpoint(lm[POSE_LANDMARKS.LEFT_HIP], lm[POSE_LANDMARKS.RIGHT_HIP]);
    const spineDeviation = calculateVerticalDeviation(shoulderMid, hipMid);

    scores.spine = scoreFromDeviation(spineDeviation, POSTURE_STANDARDS.spineAngle);

    if (spineDeviation > POSTURE_STANDARDS.spineAngle.fair) {
        issues.push({
            type: PostureIssueType.SPINE_TILT,
            severity: determineSeverity(spineDeviation, 10, 15),
            message: `脊椎側傾 ${spineDeviation.toFixed(1)}° — 請均勻分配體重，想像脊椎是一根直立的旗桿`,
            exercise: 'lateral_stretch',
            offsetValue: spineDeviation,
        });
    }

    // ─── 4. 髖骨水平評估 ────────────────────────────────────
    // 原理：比較左右髖骨的 Y 坐標差值
    const hipLevelDiff =
        Math.abs(lm[POSE_LANDMARKS.LEFT_HIP].y - lm[POSE_LANDMARKS.RIGHT_HIP].y) * 100;

    scores.hips = scoreFromDeviation(hipLevelDiff, POSTURE_STANDARDS.hipLevel);

    if (hipLevelDiff > POSTURE_STANDARDS.hipLevel.fair) {
        issues.push({
            type: PostureIssueType.PELVIC_TILT,
            severity: determineSeverity(hipLevelDiff, 7, 10),
            message: `骨盆傾斜（偏差 ${hipLevelDiff.toFixed(1)}%）— 請平均用力於雙腳，確認體重均等`,
            exercise: 'hip_level_exercise',
            offsetValue: hipLevelDiff,
        });
    }

    // ─── 5. 計算加權總分 ─────────────────────────────────────
    const totalScore = Math.round(
        scores.head      * POSTURE_STANDARDS.headForwardOffset.weight +
        scores.shoulders * POSTURE_STANDARDS.shoulderLevel.weight +
        scores.spine     * POSTURE_STANDARDS.spineAngle.weight +
        scores.hips      * POSTURE_STANDARDS.hipLevel.weight
    );

    return {
        scores,
        totalScore,
        grade: scoreToGrade(totalScore),
        issues,
        timestamp: Date.now(),
    };
}
